import { RestrictToHorizontalAxis } from '@dnd-kit/abstract/modifiers'
import { PointerActivationConstraints, PointerSensor } from '@dnd-kit/dom'
import type { DragEndEvent } from '@dnd-kit/dom'
import { move } from '@dnd-kit/helpers'
import { DragDropProvider } from '@dnd-kit/react'
import { useSortable } from '@dnd-kit/react/sortable'
import { createContext, useContext, useLayoutEffect, useRef } from 'react'
import type { HTMLAttributes, PointerEvent, ReactNode, RefObject } from 'react'

import {
  columnMaxWidth,
  columnWidthVar,
  headerMinWidth,
  JUSTIFY_BY_ALIGN,
  resizedColumnWidth
} from './treadmillMath'
import type { ColumnPins, HeaderPinOffsets } from './treadmillMath'
import { useTreadmillStyles } from './treadmillStyles'

const HEADER_SENSORS = [
  PointerSensor.configure({
    activationConstraints: [
      new PointerActivationConstraints.Distance({ value: 8 })
    ]
  })
]

type ResizeDrag = {
  columnKey: string
  startX: number
  startWidth: number
  minWidth: number
  columns: HTMLElement[]
  tables: HTMLElement[]
  handle: HTMLElement
}

type HeaderContextValue = {
  columnIndexes: Map<string, number>
  pinOffsets: HeaderPinOffsets | undefined
  columnPins: ColumnPins
  reorderable: boolean
  beginResize: (input: ResizeDrag) => void
}

const ColumnHeaderContext = createContext<HeaderContextValue | null>(null)

type ProviderProps = {
  rootRef: RefObject<HTMLElement | null>
  columnKeys: string[]
  containerWidth: number
  totalWidth: number
  onResize: (columnKey: string, width: number) => void
  onReorder: (orderedKeys: string[]) => void
  pinOffsets?: HeaderPinOffsets
  columnPins: ColumnPins
  reorderable?: boolean
  children: ReactNode
}

export const ColumnHeaderProvider = ({
  rootRef,
  columnKeys,
  containerWidth,
  totalWidth,
  onResize,
  onReorder,
  pinOffsets,
  columnPins,
  reorderable = true,
  children
}: ProviderProps) => {
  const { styles } = useTreadmillStyles()
  const dragRef = useRef<ResizeDrag | null>(null)

  const columnIndexes = new Map(
    columnKeys.map((key, index): [string, number] => [key, index])
  )

  useLayoutEffect(() => {
    const root = rootRef.current

    if (dragRef.current || !root) {
      return
    }

    for (let index = root.style.length - 1; index >= 0; index -= 1) {
      const property = root.style[index]

      if (property.startsWith('--twc-')) {
        root.style.removeProperty(property)
      }
    }
  }, [rootRef, columnKeys, containerWidth, totalWidth])

  const beginResize: HeaderContextValue['beginResize'] = (drag) => {
    const root = rootRef.current

    const maxWidth = columnMaxWidth(containerWidth, drag.minWidth)

    dragRef.current = drag
    drag.handle.classList.add(styles.resizeHandleActive)

    let frame: number | null = null
    let width = drag.startWidth
    let moved = false

    const apply = () => {
      frame = null

      if (!root) {
        return
      }

      const total = totalWidth + width - drag.startWidth

      root.style.setProperty(columnWidthVar(drag.columnKey), `${width}px`)
      root.style.setProperty('--twc-total', `${total}px`)

      for (const column of drag.columns) {
        column.style.width = `${width}px`
      }

      if (totalWidth) {
        for (const table of drag.tables) {
          table.style.width = `${total}px`
        }
      }
    }

    const handleMove = (event: globalThis.PointerEvent) => {
      moved = true
      width = resizedColumnWidth(
        drag.startWidth,
        drag.startX,
        event.clientX,
        drag.minWidth,
        maxWidth
      )

      if (frame === null) {
        frame = requestAnimationFrame(apply)
      }
    }

    const swallowClick = (clickEvent: MouseEvent) => {
      clickEvent.stopPropagation()
      clickEvent.preventDefault()
    }

    const handleUp = () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      drag.handle.classList.remove(styles.resizeHandleActive)

      if (frame !== null) {
        cancelAnimationFrame(frame)
      }

      dragRef.current = null

      if (!moved) {
        return
      }

      apply()
      window.addEventListener('click', swallowClick, {
        capture: true,
        once: true
      })
      setTimeout(() =>
        window.removeEventListener('click', swallowClick, { capture: true })
      )

      if (width !== drag.startWidth) {
        onResize(drag.columnKey, width)
      }
    }

    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    if (event.canceled) {
      return
    }

    const reordered = move(columnKeys, event)

    if (reordered !== columnKeys) {
      onReorder(reordered)
    }
  }

  return (
    <ColumnHeaderContext
      value={{
        columnIndexes,
        pinOffsets,
        columnPins,
        reorderable,
        beginResize
      }}
    >
      {reorderable ? (
        <DragDropProvider
          sensors={HEADER_SENSORS}
          modifiers={[RestrictToHorizontalAxis]}
          onDragEnd={handleDragEnd}
        >
          {children}
        </DragDropProvider>
      ) : (
        children
      )}
    </ColumnHeaderContext>
  )
}

const sectionTablesOf = (cell: HTMLTableCellElement): HTMLElement[] => [
  ...(cell
    .closest('.ant-table-container')
    ?.querySelectorAll<HTMLElement>(':scope > div > table') ?? [])
]

const measureHeaderContentWidth = (
  cell: HTMLTableCellElement,
  label: HTMLElement
): number => {
  const probe = label.cloneNode(true) as HTMLElement

  probe.style.position = 'absolute'
  probe.style.visibility = 'hidden'
  probe.style.pointerEvents = 'none'
  probe.style.width = 'max-content'
  probe.style.maxWidth = 'none'

  cell.append(probe)

  const { width } = probe.getBoundingClientRect()

  probe.remove()

  return width
}

const headerCellMinWidth = (
  cell: HTMLTableCellElement,
  label: HTMLElement
): number => {
  const { paddingLeft, paddingRight } = window.getComputedStyle(cell)

  return headerMinWidth(
    measureHeaderContentWidth(cell, label),
    parseFloat(paddingLeft) + parseFloat(paddingRight)
  )
}

type HeaderCellProps = HTMLAttributes<HTMLTableCellElement> & {
  'data-column-key'?: string
}

type CellProps = HeaderCellProps & {
  columnKey: string
  header: HeaderContextValue
}

type SortableProps = CellProps & {
  index: number
}

const useResizeHandle = (
  cellRef: RefObject<HTMLTableCellElement | null>,
  labelRef: RefObject<HTMLSpanElement | null>,
  columnKey: string,
  header: HeaderContextValue
) => {
  return (event: PointerEvent<HTMLSpanElement>) => {
    const cell = cellRef.current
    const label = labelRef.current
    const width = cell?.getBoundingClientRect().width

    if (!cell || !label || !width) {
      return
    }

    const tables = sectionTablesOf(cell)

    event.preventDefault()
    event.stopPropagation()
    header.beginResize({
      columnKey,
      startX: event.clientX,
      startWidth: width,
      minWidth: headerCellMinWidth(cell, label),
      handle: event.currentTarget,
      tables,
      columns: tables.flatMap(
        (table) =>
          table.querySelector<HTMLElement>(
            `:scope > colgroup > col:nth-child(${cell.cellIndex + 1})`
          ) ?? []
      )
    })
  }
}

const labelJustifyStyle = (style: HeaderCellProps['style']) => {
  const justifyContent = style?.textAlign && JUSTIFY_BY_ALIGN[style.textAlign]

  return justifyContent ? { justifyContent } : undefined
}

const SortableHeaderCell = ({
  columnKey,
  index,
  header,
  children,
  ...props
}: SortableProps) => {
  const { styles, cx } = useTreadmillStyles()
  const cellRef = useRef<HTMLTableCellElement | null>(null)
  const labelRef = useRef<HTMLSpanElement | null>(null)
  const { ref, handleRef, isDragging } = useSortable({
    id: columnKey,
    index,
    type: 'column',
    accept: 'column'
  })

  const handleResizePointerDown = useResizeHandle(
    cellRef,
    labelRef,
    columnKey,
    header
  )

  const attachCell = (node: HTMLTableCellElement | null) => {
    cellRef.current = node
    ref(node)
  }

  const attachLabel = (node: HTMLSpanElement | null) => {
    labelRef.current = node
    handleRef(node)
  }

  return (
    <th
      {...props}
      ref={attachCell}
      className={cx(props.className, isDragging && styles.draggingHeaderCell)}
    >
      <span
        ref={attachLabel}
        className={cx(styles.headerLabel, styles.headerGrip)}
        style={labelJustifyStyle(props.style)}
      >
        {children}
      </span>

      <span
        className={styles.resizeHandle}
        onPointerDown={handleResizePointerDown}
        onClick={(event) => event.stopPropagation()}
      />
    </th>
  )
}

const StaticHeaderCell = ({
  columnKey,
  header,
  children,
  ...props
}: CellProps) => {
  const { styles } = useTreadmillStyles()
  const cellRef = useRef<HTMLTableCellElement | null>(null)
  const labelRef = useRef<HTMLSpanElement | null>(null)
  const handleResizePointerDown = useResizeHandle(
    cellRef,
    labelRef,
    columnKey,
    header
  )

  return (
    <th {...props} ref={cellRef}>
      <span
        ref={labelRef}
        className={styles.headerLabel}
        style={labelJustifyStyle(props.style)}
      >
        {children}
      </span>

      <span
        className={styles.resizeHandle}
        onPointerDown={handleResizePointerDown}
        onClick={(event) => event.stopPropagation()}
      />
    </th>
  )
}

export const ResizableHeaderCell = ({
  children,
  ...props
}: HeaderCellProps) => {
  const header = useContext(ColumnHeaderContext)
  const columnKey = props['data-column-key']
  const index = columnKey ? (header?.columnIndexes.get(columnKey) ?? -1) : -1
  const pin = columnKey ? header?.pinOffsets?.get(columnKey) : undefined
  const style = pin ? { ...props.style, ...pin } : props.style

  if (!header || !columnKey || index < 0) {
    return (
      <th {...props} style={style}>
        {children}
      </th>
    )
  }

  if (!header.reorderable || header.columnPins[columnKey]) {
    return (
      <StaticHeaderCell
        {...props}
        style={style}
        columnKey={columnKey}
        header={header}
      >
        {children}
      </StaticHeaderCell>
    )
  }

  return (
    <SortableHeaderCell
      {...props}
      style={style}
      columnKey={columnKey}
      index={index}
      header={header}
    >
      {children}
    </SortableHeaderCell>
  )
}
